import {
  Alert,
  Actions,
  Button,
  Text,
  TextField,
  Stack,
  IconStar,
} from 'braid-design-system';
import React, { type FormEventHandler } from 'react';
import { useTranslations } from '@vocab/react';
import translations from './.vocab';
import {
  notConsecutiveCharacters,
  minLength,
  type Validator,
} from '@seek/validators-js';
import type { PasswordUseField } from './FormContainer';
import { MINIMUM_PASSWORD_LENGTH } from './helpers';
import type { ErrorMessage } from 'src/types';
import ServerError from '../../ServerError/ServerError';

export const FORM_NAME = 'changePassword';

interface Props {
  onSubmit: FormEventHandler;
  error?: ErrorMessage;
  toggle: () => void;
  setPasswordFields: (value: string) => void;
  useField: PasswordUseField;
  submitting: boolean;
}

const generatePassword = () => {
  const charset = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0, n = charset.length; i < MINIMUM_PASSWORD_LENGTH; ++i) {
    result += charset.charAt(Math.floor(Math.random() * n));
  }
  return result;
};

const CustomerServiceForm = ({
  onSubmit,
  error,
  toggle,
  setPasswordFields,
  useField,
  submitting,
}: Props) => {
  const { t } = useTranslations(translations);

  const newPassword = useField({
    id: 'newPassword',
    validators: [
      minLength,
      notConsecutiveCharacters as Validator<
        string,
        Record<string, any>,
        Record<string, any>,
        string
      >,
    ],
    validatorProps: {
      min: MINIMUM_PASSWORD_LENGTH,
      consecutive: 2,
    },
  });
  const onGenerateClicked = () => {
    const password = generatePassword();
    setPasswordFields(password);
  };
  return (
    <form onSubmit={onSubmit}>
      <Stack space="medium">
        <Stack space="medium">
          <Alert>
            <Text>{t('CS users can generate a random password')}</Text>
          </Alert>
          <Button
            tone="formAccent"
            variant="transparent"
            type="button"
            onClick={onGenerateClicked}
            icon={<IconStar active />}
          >
            {t('Generate Password')}
          </Button>
          <TextField
            id="newPassword"
            name="newPassword"
            label={t('New password')}
            onChange={() => {}}
            value={newPassword.value}
            tone={Boolean(newPassword.valid) ? 'neutral' : 'critical'}
            message={newPassword.errorMessage}
          />
          {error && !submitting ? <ServerError error={error} /> : null}
        </Stack>

        <Actions>
          <Button type="submit" tone="formAccent" loading={submitting}>
            {t('Update password')}
          </Button>

          <Button variant="ghost" tone="formAccent" onClick={toggle}>
            {t('Cancel')}
          </Button>
        </Actions>
      </Stack>
    </form>
  );
};

export default CustomerServiceForm;
